import React, { useEffect, useState } from 'react';

import { Dropdown, Modal, Table } from 'react-bootstrap';
import { CustomButton, Loading } from '../../../_common';
import {
    StageDetailsForEditStages,
    GetWellheadsByJobId_getWellheadsByJobId as Wellheads,
    GetWellheadsByStageGroupId,
    GetWellheadsByStageGroupId_getWellheadsByStageGroupId as StageWellheads,
} from '../../../../types/schemaTypes';
import '../JobStageSummary.scss';
import EditStageModalRow from './EditStageModalRow';
import { GET_WELL_HEADS_BY_STAGE_GROUP_ID } from '../../../../graphql/client/queries';
import { useQuery } from '@apollo/client/react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

interface EditStageModalProps {
    showEditStageModal: boolean;
    wellheads: Wellheads[];
    stageGroupId: number | undefined;
    handleOnClose: (modalType) => void;
    handleOnSave: (fracType, stageDetails) => void;
    mutationLoading: boolean;
    modalError: string;
    setModalError: React.Dispatch<React.SetStateAction<string>>;
    JobId: number;
}

interface StagesToEditProps {
    index: number;
    jobStageId: number;
    wellHead: string;
    stageNum: number;
    stageSheetId: number;
    stageSheetDescrip: string;
    maxStageNum: number;
}

const EditStageModal = ({
    showEditStageModal,
    wellheads,
    stageGroupId,
    handleOnClose,
    handleOnSave,
    mutationLoading,
    modalError,
    setModalError,
    JobId,
}: EditStageModalProps): React.ReactElement => {
    const [editStageCount, setEditStageCount] = useState(0);
    const [stageCount, setStageCount] = useState(0);

    const [showDelete, setShowDelete] = useState(false);
    const [stagesToEdit, setStagesToEdit] = useState<StagesToEditProps[]>();

    useEffect(() => {
        if (stagesToEdit && stagesToEdit.length > 1) {
            setShowDelete(true);
        } else {
            setShowDelete(false);
        }
    }, [stagesToEdit]);

    const [fracType, setFracType] = useState<string>();
    const [fracTypeError, setFracTypeError] = useState('');

    const [wellheadError, setWellheadError] = useState<number[]>();
    const [stageNumberNotAnIntegerError, setStageNumberNotAnIntegerError] = useState<number[]>();
    const [stageNumberOutOfBoundsError, setStageNumberOutOfBoundsError] = useState<number[]>();
    const [stageNumberMaxError, setStageNumberMaxError] = useState<number[]>();
    const [stageNumberDuplicateError, setStageNumberDuplicateError] = useState<number[]>();
    const [wellheadDuplicateError, setWellheadDuplicateError] = useState<number[]>();
    const [runsheetDuplicateError, setRunsheetDuplicateError] = useState<number[]>();
    const [fracTypeTooltipMsg, setFracTypeTooltipMsg] = useState('');

    const { loading, error, data } = useQuery<GetWellheadsByStageGroupId>(GET_WELL_HEADS_BY_STAGE_GROUP_ID, {
        variables: {
            stageGroupId: stageGroupId,
        },
        fetchPolicy: 'network-only',
    });
    let stageItems: StageWellheads[] = [];
    const rawItems = (data?.getWellheadsByStageGroupId ?? []).filter((s): s is StageWellheads => s != null);
    if (rawItems.length > 0) {
        if (!fracType) {
            const first = rawItems[0];
            setFracType(first.fracType);
            if (first.fracType === 'Single') {
                setFracTypeTooltipMsg('One wellhead, one stand');
            } else if (first.fracType === 'Simul') {
                setFracTypeTooltipMsg('Two wellheads, one stand');
            } else if (first.fracType === 'Dual') {
                setFracTypeTooltipMsg('Two wellheads, two stands');
            }
        }
        stageItems = rawItems;
        if (stageCount === 0) setStageCount(stageItems.length);
    }

    const stageSheetIds = {
        runsheet1: -1,
        runsheet2: -1,
    };
    stageItems.map(stageItem => {
        if (stageSheetIds.runsheet1 === -1 && stageItem.stageSheetDescrip === 'RunSheet1')
            stageSheetIds.runsheet1 = stageItem.stageSheetId;
        else if (stageSheetIds.runsheet2 === -1 && stageItem.stageSheetDescrip === 'RunSheet2')
            stageSheetIds.runsheet2 = stageItem.stageSheetId;
    });

    useEffect(() => {
        setStagesToEdit([]);
        stageItems[0] && setFracType(stageItems[0].fracType);
    }, []);

    const handleOnChangeFracType = (fracType: string) => {
        setModalError('');
        setFracType(fracType);
        setFracTypeError('');
        if (fracType === 'Single') {
            setFracTypeTooltipMsg('One wellhead, one stand');
        } else if (fracType === 'Simul') {
            setFracTypeTooltipMsg('Two wellheads, one stand');
        } else if (fracType === 'Dual') {
            setFracTypeTooltipMsg('Two wellheads, two stands');
        }
    };

    const onClickClose = () => {
        setFracTypeError('');
        handleOnClose('editStage');
    };

    const onClickSave = () => {
        let err = false;
        setModalError('');
        // Remove duplicate entries by index
        if (stagesToEdit) {
            const seenIndexes = new Set<number>();
            const uniqueStages = stagesToEdit.filter(s => {
                if (seenIndexes.has(s.index)) return false;
                seenIndexes.add(s.index);
                return true;
            });
            if (uniqueStages.length !== stagesToEdit.length) {
                setStagesToEdit(uniqueStages);
            }
        }

        if (!fracType) {
            setFracTypeError('Select the Frac Type');
            err = true;
        }

        if (fracType === 'Single' && stagesToEdit && stagesToEdit.length !== 1) {
            setModalError("Frac type 'Single' must have only one Stage.");
            err = true;
        }

        if (fracType === 'Simul' && stagesToEdit && stagesToEdit.length < 2) {
            setModalError("Frac type 'Simul' must have at least two Stages.");
            err = true;
        }

        if (fracType === 'Dual' && stagesToEdit && stagesToEdit.length < 2) {
            setModalError("Frac type 'Dual' must have at least two Stage Sheets.");
            err = true;
        }

        if (fracType === 'Dual' && stagesToEdit) {
            let sheet1 = false;
            let sheet2 = false;
            stagesToEdit.map(stage => {
                if (stage.stageSheetDescrip === 'Runsheet 1') sheet1 = true;
                if (stage.stageSheetDescrip === 'Runsheet 2') sheet2 = true;
            });
            if (!sheet1 || !sheet2) {
                setModalError("Frac type 'Dual' must have at least two Stage Sheets.");
                err = true;
            }
        }

        if (stagesToEdit) {
            const stageDetails: StageDetailsForEditStages[] = [];
            const tempWellheadError: number[] = [];
            const tempStageNumberNotAnIntegerError: number[] = [];
            const tempStageNumberOutOfBoundsError: number[] = [];
            const tempStageNumberMaxError: number[] = [];
            const tempStageNumberDuplicateError: number[] = [];
            const tempRunnerNumberDuplicateError: number[] = [];
            const tempWellheadDuplicateError: number[] = [];

            for (const stageToEdit of stagesToEdit) {
                if (stageToEdit.wellHead === '') {
                    tempWellheadError.push(stageToEdit.index);
                    err = true;
                }

                if (stageToEdit.stageNum === -1) {
                    tempStageNumberNotAnIntegerError.push(stageToEdit.index);
                    err = true;
                } else {
                    for (const wellhead of wellheads) {
                        if (
                            wellhead.stages &&
                            stageToEdit.wellHead === wellhead.wellHead &&
                            stageToEdit.stageNum > wellhead.stages
                        ) {
                            tempStageNumberOutOfBoundsError.push(stageToEdit.index);
                            err = true;
                            break;
                        }
                    }
                }

                if (stageToEdit.stageNum > stageToEdit.maxStageNum) {
                    tempStageNumberMaxError.push(stageToEdit.index);
                    err = true;
                }

                for (const stage of stagesToEdit) {
                    if (
                        stageToEdit.wellHead &&
                        stageToEdit.stageNum !== -1 &&
                        stageToEdit.index !== stage.index &&
                        stageToEdit.index > stage.index &&
                        stageToEdit.wellHead === stage.wellHead &&
                        stageToEdit.stageNum === stage.stageNum
                    ) {
                        tempStageNumberDuplicateError.push(stageToEdit.index);
                        err = true;
                    }

                    if (
                        stageToEdit.wellHead &&
                        stageToEdit.stageNum !== -1 &&
                        stageToEdit.index !== stage.index &&
                        stageToEdit.index > stage.index &&
                        stageToEdit.wellHead === stage.wellHead
                    ) {
                        tempWellheadDuplicateError.push(stageToEdit.index);
                        err = true;
                    }
                }

                setWellheadError(tempWellheadError);
                setStageNumberNotAnIntegerError(tempStageNumberNotAnIntegerError);
                setStageNumberOutOfBoundsError(tempStageNumberOutOfBoundsError);
                setStageNumberMaxError(tempStageNumberMaxError);
                setStageNumberDuplicateError(tempStageNumberDuplicateError);
                setWellheadDuplicateError(tempWellheadDuplicateError);
                setRunsheetDuplicateError(tempRunnerNumberDuplicateError);
                if (!err) {
                    stageDetails.push({
                        stageId: stageToEdit.jobStageId,
                        wellHead: stageToEdit.wellHead,
                        stageNum: stageToEdit.stageNum,
                        stageSheetId: stageToEdit.stageSheetId,
                        stageSheetDescrip: stageToEdit.stageSheetDescrip === 'Runsheet 1' ? 'RunSheet1' : 'RunSheet2',
                    });
                }
            }
            if (!err) handleOnSave(fracType, stageDetails);
        }
    };

    const editStageRowElement = () => {
        const element: React.ReactElement[] = [];
        stageItems.map((stageItem, index) => {
            element.push(
                <EditStageModalRow
                    JobId={JobId}
                    key={index}
                    index={index}
                    fracType={fracType}
                    stageItem={stageItem}
                    wellheads={wellheads}
                    stageSheetIds={stageSheetIds}
                    stagesToEdit={stagesToEdit}
                    setStagesToEdit={setStagesToEdit}
                    stageCount={stageCount}
                    setStageCount={setStageCount}
                    showDelete={showDelete}
                    wellheadError={wellheadError}
                    setWellheadError={setWellheadError}
                    stageNumberNotAnIntegerError={stageNumberNotAnIntegerError}
                    setStageNumberNotAnIntegerError={setStageNumberNotAnIntegerError}
                    stageNumberOutOfBoundsError={stageNumberOutOfBoundsError}
                    setStageNumberOutOfBoundsError={setStageNumberOutOfBoundsError}
                    stageNumberDuplicateError={stageNumberDuplicateError}
                    setStageNumberDuplicateError={setStageNumberDuplicateError}
                    wellheadDuplicateError={wellheadDuplicateError}
                    setWellheadDuplicateError={setWellheadDuplicateError}
                    runsheetDuplicateError={runsheetDuplicateError}
                    setRunsheetDuplicateError={setRunsheetDuplicateError}
                    stageNumberMaxError={stageNumberMaxError}
                    setStageNumberMaxError={setStageNumberMaxError}
                />,
            );
        });
        for (let index = 0; index < editStageCount; index++) {
            element.push(
                <EditStageModalRow
                    JobId={JobId}
                    key={stageItems.length + index}
                    index={stageItems.length + index}
                    fracType={fracType}
                    wellheads={wellheads}
                    stageSheetIds={stageSheetIds}
                    stagesToEdit={stagesToEdit}
                    setStagesToEdit={setStagesToEdit}
                    stageCount={stageCount}
                    setStageCount={setStageCount}
                    showDelete={showDelete}
                    wellheadError={wellheadError}
                    setWellheadError={setWellheadError}
                    stageNumberNotAnIntegerError={stageNumberNotAnIntegerError}
                    setStageNumberNotAnIntegerError={setStageNumberNotAnIntegerError}
                    stageNumberOutOfBoundsError={stageNumberOutOfBoundsError}
                    setStageNumberOutOfBoundsError={setStageNumberOutOfBoundsError}
                    stageNumberDuplicateError={stageNumberDuplicateError}
                    setStageNumberDuplicateError={setStageNumberDuplicateError}
                    wellheadDuplicateError={wellheadDuplicateError}
                    setWellheadDuplicateError={setWellheadDuplicateError}
                    runsheetDuplicateError={runsheetDuplicateError}
                    setRunsheetDuplicateError={setRunsheetDuplicateError}
                    stageNumberMaxError={stageNumberMaxError}
                    setStageNumberMaxError={setStageNumberMaxError}
                />,
            );
        }
        return element;
    };

    return (
        <Modal show={showEditStageModal} onHide={onClickClose} dialogClassName="AddStageModal-Container">
            {(mutationLoading || loading) && (
                <span className="AddStageModal-Loading">
                    <Loading />
                </span>
            )}

            <Modal.Header className="AddStageModal-Header">
                <Modal.Title className="AddStageModal-Header-Container">
                    <button className="material-icons AddStageModal-X-Button" onClick={onClickClose}>
                        close
                    </button>
                    <div className="AddStageModal-Title">Edit Stages</div>
                    <div className="AddStageModal-Error">{modalError}</div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="AddStageModal-Body">
                <div className="Textfield-InputTitle input-group-text">Frac Type</div>
                <div className="AddStageModal-info">
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-dark" id="dropdown-basic" className="AddStageModal-Dropdown">
                            <span>{fracType ?? 'Select Frac Type'}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {wellheads.length === 1 ? (
                                <Dropdown.Item 
                               // onSelect={() => handleOnChangeFracType('Single')}
                               onClick={() => handleOnChangeFracType('Single')}
                                >Single</Dropdown.Item>
                            ) : (
                                <>
                                    <Dropdown.Item 
                                    //onSelect={() => handleOnChangeFracType('Single')}
                                    onClick={() => handleOnChangeFracType('Single')}
                                    >
                                        Single
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                   // onSelect={() => handleOnChangeFracType('Simul')}
                                   onClick={() => handleOnChangeFracType('Simul')}
                                    >
                                        Simul
                                    </Dropdown.Item>
                                    <Dropdown.Item 
                                   // onSelect={() => handleOnChangeFracType('Dual')}
                                   onClick={() => handleOnChangeFracType('Dual')}
                                    >Dual</Dropdown.Item>
                                </>
                            )}
                        </Dropdown.Menu>
                        <div className="AddStageModal-Error AddStageModal-Fractype-Error">{fracTypeError}</div>
                    </Dropdown>
                    <OverlayTrigger
                        placement="right"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<span className="AddStageModal-DirectionsTooltip">{fracTypeTooltipMsg}</span>}
                    >
                        <div className="NavBar-Tooltip-Container">
                            {/* <TextContent title={''} value="" showInfoIcon={true}></TextContent> */}
                            <i className="material-icons AssignStandByDriverModal-InfoIcon">info</i>
                        </div>
                    </OverlayTrigger>
                </div>
                {fracType && fracType !== 'Single' && (
                    <CustomButton
                        title={'Add Stage'}
                        onClick={() => {
                            setStageCount(stageCount + 1);
                            setEditStageCount(editStageCount + 1);
                        }}
                        isDisabled={stageCount === wellheads.length}
                    />
                )}

                <Table striped bordered className="tabletest AddStageModal-Table">
                    <thead>
                        <tr>
                            <th>Wellhead</th>
                            <th>Stage Number</th>
                            <th>Runsheet</th>
                            <th>Delete</th>
                        </tr>
                    </thead>
                    <tbody data-bind="foreach: teams">{editStageRowElement()}</tbody>
                </Table>
            </Modal.Body>

            <Modal.Footer className="AddStageModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={onClickClose} isDisabled={mutationLoading} />
                <CustomButton title="Save" onClick={onClickSave} isDisabled={mutationLoading} />
            </Modal.Footer>
        </Modal>
    );
};

export default EditStageModal;
